import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom, Observable } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import { PaginatedResponse, Task, TaskCount, TaskListParams } from '@core/models';

/** Tareas (`/api/tasks/`). Keeps the user's open count for the menu. */
@Injectable({ providedIn: 'root' })
export class TaskService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'tasks';

  /** The user's open tasks and how many are overdue, for the menu's badge. */
  readonly count = signal<TaskCount | null>(null);

  private send<T>(call: Observable<T>): Promise<T> {
    return firstValueFrom(
      call.pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  list(params: TaskListParams = {}): Promise<PaginatedResponse<Task>> {
    const query: Record<string, string> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && value !== '') query[key] = String(value);
    }
    return this.send(this.http.get<PaginatedResponse<Task>>(`${this.baseUrl}/`, { params: query }));
  }

  async refreshCount(): Promise<void> {
    try {
      this.count.set(await this.send(this.http.get<TaskCount>(`${this.baseUrl}/count/`)));
    } catch {
      // The badge is a convenience: without it the menu still works.
    }
  }

  candidates(id: number): Promise<{ id: number; name: string }[]> {
    return this.send(
      this.http.get<{ id: number; name: string }[]>(`${this.baseUrl}/${id}/candidates/`),
    );
  }

  reassign(id: number, user: number): Promise<Task> {
    return this.send(this.http.post<Task>(`${this.baseUrl}/${id}/reassign/`, { user }));
  }

  discard(id: number, note: string): Promise<Task> {
    return this.send(this.http.post<Task>(`${this.baseUrl}/${id}/discard/`, { note }));
  }

  /** A document: `ok` or its fault. A high-cost review: `corrected` / `justified`. */
  decide(id: number, decision: string, note = ''): Promise<Task> {
    return this.send(this.http.post<Task>(`${this.baseUrl}/${id}/decide/`, { decision, note }));
  }
}
